import accountSettingApi from '../api/accountSettingApi';
import { IAccountSetting } from '../interfaces/IAccountSetting';
import { IApiResponse } from '../interfaces/IApiResponse';
import accountSetting from '../models/accountSetting';

interface ConnectAccountParams {
  accessToken: string;
  code?: string;
  id?: string;
}

class AccountSettingService {
  getUserPhone(): Promise<IApiResponse<IAccountSetting>> {
    return accountSettingApi
      .getUserPhone()
      .then((res: IApiResponse) => {
        if (res.success) {
          if (res.data) {
            return {
              success: res.success,
              data: accountSetting.mappingObjectNumberPhone(res.data),
            };
          }
          return { success: res.success, data: res.data || [] };
        }
        console.log('ERROR: accountSettingService/getUserPhone:', res);
        return { success: res.success, data: res.data || [] };
      })
      .catch((e: Error) => {
        console.warn('ERROR: accountSettingService/getUserPhone:', e);
        throw new Error('fail');
      });
  }

  postUpdateUserPhone(params: any): Promise<IApiResponse<IAccountSetting>> {
    return accountSettingApi.postUpdateUserPhone(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: accountSetting.mappingObjectNumberPhone(res.data),
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/postUpdateUserPhone:', res);
      }
      return { success: false };
    });
  }

  postVerifyUserPhone(params: any): Promise<IApiResponse> {
    return accountSettingApi.postVerifyUserPhone(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/postVerifyUserPhone:', res);
      }
      return { success: false };
    });
  }

  postVerifyResendUserPhone(params: any): Promise<IApiResponse> {
    return accountSettingApi.postVerifyResendUserPhone(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/postVerifyResendUserPhone:', res);
      }
      return { success: false };
    });
  }

  postValidatePhoneNumber(params: any): Promise<IApiResponse> {
    return accountSettingApi.postValidatePhoneNumber(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/postValidatePhoneNumber:', res);
      }
      return { success: false };
    });
  }

  patchEmailAddress(params: any): Promise<IApiResponse> {
    return accountSettingApi.patchEmailAddress(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/patchEmailAddress:', res);
      }
      return { success: false };
    });
  }

  patchPassword(params: any): Promise<IApiResponse> {
    return accountSettingApi.patchPassword(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/patchPassword:', res);
      }
      return { success: false };
    });
  }

  createPassword(params: any): Promise<IApiResponse> {
    return accountSettingApi.createPassword(params).then((res: IApiResponse) => {
      try {
        return res;
      } catch (e) {
        console.log('ERROR: accountSettingService/createPassword:', res);
      }
      return { success: false };
    });
  }

  patchUserName(params: any): Promise<IApiResponse> {
    return accountSettingApi.patchUserName(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/patchUserName:', res);
      }
      return { success: false };
    });
  }

  deletePhoneNumber(): Promise<IApiResponse> {
    return accountSettingApi.deletePhoneNumber();
  }

  postPauseAccount(params?: any): Promise<IApiResponse> {
    return accountSettingApi.postPauseAccount(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/postPauseAccount:', res);
      }
      return { success: false };
    });
  }

  postUnPauseAccount(): Promise<IApiResponse> {
    return accountSettingApi.postUnPauseAccount().then((res: IApiResponse) => {
      try {
        return res;
      } catch (e) {
        console.log('ERROR: accountSettingService/postUnPauseAccount:', res);
      }
      return { success: false };
    });
  }

  deleteAccount(params: any): Promise<IApiResponse> {
    return accountSettingApi.deleteAccount(params).then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          data: res.data,
          message: res.message,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/deleteAccount:', res);
      }
      return { success: false };
    });
  }

  connectWithAppleAccount(params?: ConnectAccountParams): Promise<IApiResponse> {
    let bodyParams: { AccessToken: string; AccessTokenSecret: string } | null = null;
    if (params) {
      bodyParams = {
        AccessToken: params.accessToken,
        AccessTokenSecret: params.code || '',
      };
    }
    if (bodyParams) {
      return accountSettingApi.connectAccountApple(bodyParams).then((res: IApiResponse) => {
        try {
          return {
            success: res.success,
            message: res.message,
            statusCode: res.statusCode,
            status: res.status,
          };
        } catch (e) {
          console.log('ERROR: accountSettingService/connectAccountApple:', res);
        }
        return { success: false };
      });
    }
    return accountSettingApi.deleteAccountApple().then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          message: res.message,
          statusCode: res.statusCode,
          status: res.status,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/deleteAccountApple:', res);
      }
      return { success: false };
    });
  }

  connectWithFacebookAccount(params?: ConnectAccountParams): Promise<IApiResponse> {
    let bodyParams: { UserID: string; AccessToken: string } | null = null;
    if (params && params.id) {
      bodyParams = {
        UserID: params.id,
        AccessToken: params.accessToken,
      };
    }
    if (bodyParams) {
      return accountSettingApi.connectAccountFacebook(bodyParams).then((res: IApiResponse) => {
        try {
          return {
            success: res.success,
            message: res.message,
            statusCode: res.statusCode,
            status: res.status,
          };
        } catch (e) {
          console.log('ERROR: accountSettingService/connectAccountFacebook:', res);
        }
        return { success: false };
      });
    }
    return accountSettingApi.deleteAccountFacebook().then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          message: res.message,
          statusCode: res.statusCode,
          status: res.status,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/deleteAccountFacebook:', res);
      }
      return { success: false };
    });
  }

  connectWithGoogleAccount(params?: ConnectAccountParams): Promise<IApiResponse> {
    let bodyParams: { AccessToken: string } | null = null;
    if (params) {
      bodyParams = {
        AccessToken: params.accessToken,
      };
    }
    if (bodyParams) {
      return accountSettingApi.connectAccountGoogle(bodyParams).then((res: IApiResponse) => {
        try {
          return {
            success: res.success,
            message: res.message,
            statusCode: res.statusCode,
            status: res.status,
          };
        } catch (e) {
          console.log('ERROR: accountSettingService/connectAccountGoogle:', res);
        }
        return { success: false };
      });
    }
    return accountSettingApi.deleteAccountGoogle().then((res: IApiResponse) => {
      try {
        return {
          success: res.success,
          message: res.message,
          statusCode: res.statusCode,
          status: res.status,
        };
      } catch (e) {
        console.log('ERROR: accountSettingService/deleteAccountGoogle:', res);
      }
      return { success: false };
    });
  }
}

export default new AccountSettingService();
